// src/Pages/Admin/Dashboard.jsx
import React, { useState, useEffect, useMemo } from "react";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../../firebase";
import "./Dashboard.scss";
import {
  FaBoxOpen,
  FaHourglassHalf,
  FaCheckDouble,
  FaIndustry,
  FaSpinner,
  FaExclamationCircle,
  FaFire,
  FaCalendarAlt,
} from "react-icons/fa";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

// --- UTILS FOR UI ---
const extractMasterThumbnail = (order) => {
  if (order.files && order.files.length > 0) return order.files[0];
  if (order.orderType === "Single" && order.singleItems?.length > 0) {
    const item = order.singleItems[0];
    if (item?.fileUrls && item.fileUrls.length > 0) return item.fileUrls[0];
    if (item?.fileUrl) return item.fileUrl;
  }
  return null;
};

const getDeliveryAlert = (dateStr) => {
  if (!dateStr) return { level: "safe", text: "No Date", days: 999 };

  const due = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time for accurate day calc

  const diffTime = due - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0)
    return {
      level: "overdue",
      text: `Overdue by ${Math.abs(diffDays)}d`,
      days: diffDays,
    };
  if (diffDays === 0)
    return { level: "urgent", text: "Due Today!", days: diffDays };
  if (diffDays <= 2)
    return { level: "urgent", text: `Due in ${diffDays}d`, days: diffDays };
  if (diffDays <= 5)
    return { level: "warning", text: `Due in ${diffDays}d`, days: diffDays };
  return { level: "safe", text: dateStr, days: diffDays };
};

const Dashboard = () => {
  const [orders, setOrders] = useState([]);
  const [parties, setParties] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsLoading(true);
      try {
        const pSnap = await getDocs(collection(db, "parties"));
        setParties(pSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

        const oSnap = await getDocs(
          query(collection(db, "partyOrders"), orderBy("createdAt", "desc")),
        );
        setOrders(oSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchDashboardData();
  }, []);

  const analytics = useMemo(() => {
    if (!orders.length) return null;

    const normalizeStatus = (s) => (s ? s.trim().toLowerCase() : "pending");

    let total = orders.length;
    let pending = 0;
    let inProduction = 0;
    let completed = 0;

    const typeCount = { Single: 0, Bulk: 0, Casting: 0 };
    const partyStats = {};
    const monthlyStats = {};
    const activeOrders = []; // For the priority list

    orders.forEach((order) => {
      const status = normalizeStatus(order.status);

      // KPI Counts
      if (status === "pending") pending++;
      else if (status === "processing" || status === "polishing")
        inProduction++;
      else if (
        status === "complete" ||
        status === "hallmarked" ||
        status === "delivered"
      )
        completed++;

      // Order Types
      if (typeCount[order.orderType] !== undefined)
        typeCount[order.orderType]++;
      else typeCount.Single++;

      // Party Stats
      const pId = order.partyId || "Unknown";
      if (!partyStats[pId])
        partyStats[pId] = { name: pId, count: 0, pending: 0, completed: 0 };
      partyStats[pId].count++;
      if (status === "pending" || status === "processing")
        partyStats[pId].pending++;
      if (status === "delivered" || status === "complete")
        partyStats[pId].completed++;

      // Monthly Trend
      let dateObj = new Date();
      if (order.createdAt?.seconds)
        dateObj = new Date(order.createdAt.seconds * 1000);
      else if (order.createdAt) dateObj = new Date(order.createdAt);

      const monthYear = dateObj.toLocaleString("default", {
        month: "short",
        year: "numeric",
      });
      if (!monthlyStats[monthYear])
        monthlyStats[monthYear] = { name: monthYear, Orders: 0 };
      monthlyStats[monthYear].Orders++;

      // Collect active orders for the Priority list
      if (status !== "delivered" && status !== "complete") {
        activeOrders.push(order);
      }
    });

    const topParties = Object.values(partyStats)
      .map((ps) => {
        const found = parties.find((p) => p.id === ps.name);
        return { ...ps, name: found ? found.name : "Unknown Party" };
      })
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    const typeData = [
      { name: "Single", value: typeCount.Single },
      { name: "Bulk", value: typeCount.Bulk },
      { name: "Casting", value: typeCount.Casting },
    ].filter((d) => d.value > 0);

    const trendData = Object.values(monthlyStats).reverse();

    // SORT ACTIVE ORDERS BY CLOSEST DELIVERY DATE
    const priorityOrders = activeOrders
      .map((o) => ({ ...o, alertData: getDeliveryAlert(o.deliveryDate) }))
      .sort((a, b) => a.alertData.days - b.alertData.days)
      .slice(0, 10); // Take top 10

    return {
      total,
      pending,
      inProduction,
      completed,
      typeData,
      topParties,
      trendData,
      priorityOrders,
    };
  }, [orders, parties]);

  const COLORS = ["#3b82f6", "#f59e0b", "#10b981", "#ef4444", "#8b5cf6"];

  if (isLoading)
    return (
      <div className="erp-dash-loading">
        <FaSpinner className="spin-icon" /> Analyzing Data...
      </div>
    );
  if (!analytics)
    return <div className="erp-dash-loading">No data available.</div>;

  return (
    <div className="erp-super-dashboard">
      <div className="dash-header">
        <div>
          <h1>Production Analytics</h1>
          <p>Real-time overview of manufacturing, orders, and party metrics.</p>
        </div>
      </div>

      {/* --- KPI CARDS --- */}
      <div className="kpi-grid">
        <div className="kpi-card total">
          <div className="kpi-info">
            <span>Total Lifetime</span>
            <h2>{analytics.total}</h2>
          </div>
          <FaBoxOpen className="kpi-icon" />
        </div>
        <div className="kpi-card pending">
          <div className="kpi-info">
            <span>Pending / Queue</span>
            <h2>{analytics.pending}</h2>
          </div>
          <FaHourglassHalf className="kpi-icon" />
        </div>
        <div className="kpi-card production">
          <div className="kpi-info">
            <span>In Production</span>
            <h2>{analytics.inProduction}</h2>
          </div>
          <FaIndustry className="kpi-icon" />
        </div>
        <div className="kpi-card complete">
          <div className="kpi-info">
            <span>Ready / Delivered</span>
            <h2>{analytics.completed}</h2>
          </div>
          <FaCheckDouble className="kpi-icon" />
        </div>
      </div>

      {/* --- PRIORITY ACTION LIST (TOP 10 UPCOMING) --- */}
      {analytics.priorityOrders.length > 0 && (
        <div className="priority-section erp-fade-up">
          <div className="section-title">
            <h3>
              <FaFire style={{ color: "#ef4444", marginRight: "8px" }} />{" "}
              Priority Action: Upcoming Deliveries
            </h3>
            <span>Next 10 active orders requiring attention</span>
          </div>

          <div className="priority-list">
            {analytics.priorityOrders.map((order) => {
              const partyName =
                parties.find((p) => p.id === order.partyId)?.name || "Unknown";
              const thumb = extractMasterThumbnail(order);
              const alert = order.alertData;

              return (
                <div
                  key={order.id}
                  className={`priority-row alert-${alert.level}`}
                >
                  <div className="pr-thumb">
                    {thumb ? (
                      <img src={thumb} alt="item" />
                    ) : (
                      <div className="no-img">N/A</div>
                    )}
                  </div>

                  <div className="pr-info">
                    <span className="pr-id">#{order.orderId}</span>
                    <span className="pr-party">{partyName}</span>
                  </div>

                  <div className="pr-meta">
                    <span className="type-badge">{order.orderType}</span>
                    <span className="status-badge">
                      {order.status || "Pending"}
                    </span>
                  </div>

                  <div className="pr-date">
                    <div className={`date-badge ${alert.level}`}>
                      {alert.level === "overdue" && <FaExclamationCircle />}
                      {alert.level === "urgent" && <FaFire />}
                      {alert.level === "warning" && <FaCalendarAlt />}
                      {alert.text}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* --- CHARTS GRID --- */}
      <div className="charts-grid">
        <div className="chart-card span-2">
          <h3>Monthly Order Volume</h3>
          <div className="chart-wrapper">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={analytics.trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="name" stroke="#94a3b8" />
                <YAxis stroke="#94a3b8" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#0f172a",
                    border: "1px solid #334155",
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="Orders"
                  stroke="#d97706"
                  strokeWidth={3}
                  dot={{ r: 5, fill: "#d97706" }}
                  activeDot={{ r: 8 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="chart-card">
          <h3>Order Categories</h3>
          <div className="chart-wrapper">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={analytics.typeData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {analytics.typeData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ backgroundColor: "#0f172a", border: "none" }}
                />
                <Legend verticalAlign="bottom" height={36} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="chart-card span-3">
          <h3>Top Parties (By Volume)</h3>
          <div className="chart-wrapper">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={analytics.topParties}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 40, bottom: 5 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#1e293b"
                  horizontal={true}
                  vertical={false}
                />
                <XAxis type="number" stroke="#94a3b8" />
                <YAxis
                  dataKey="name"
                  type="category"
                  stroke="#94a3b8"
                  width={100}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#0f172a",
                    border: "1px solid #334155",
                  }}
                  cursor={{ fill: "rgba(255,255,255,0.05)" }}
                />
                <Legend />
                <Bar
                  dataKey="completed"
                  name="Completed Work"
                  stackId="a"
                  fill="#10b981"
                  radius={[0, 0, 0, 0]}
                />
                <Bar
                  dataKey="pending"
                  name="Pending/In-Work"
                  stackId="a"
                  fill="#f59e0b"
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
